import { useEffect, useState } from "react";
import { func, node, string, bool, oneOf, object } from "prop-types";
import { useHistory } from "react-router";
import useModuleSubscription from "../../hooks/useModuleSubscription";
import LockIconBadge from "../LockIconBadge";
import {
  MODULE_QUERY_PARAM,
  SUBSCRIPTION_ADDONS_PATH,
} from "../../app/settings/components/Subscriptions/ActiveSubscription/constants";
import styles from "./ModuleGate.module.scss";

const joinClasses = (...classNames) => classNames.filter(Boolean).join(" ");

const ModuleGate = ({
  name,
  children,
  renderGate,
  showLockIcon = true,
  position = "end",
  availableToTrial = true,
  lockOnExpiredTrial = true,
}) => {
  const history = useHistory();
  const { hasModule, hasActiveModule, hasExpiredTrialModule, loading } =
    useModuleSubscription(name);
  const [resolvedHasModule, setResolvedHasModule] = useState(null);

  const isExpiredTrial = lockOnExpiredTrial && hasExpiredTrialModule;
  const canAccessModule =
    !isExpiredTrial && (availableToTrial ? hasModule : hasActiveModule);

  useEffect(() => {
    if (!loading) {
      setResolvedHasModule(Boolean(canAccessModule));
    }
  }, [loading, canAccessModule]);

  const openModuleInAddons = () => {
    history.push(
      `${SUBSCRIPTION_ADDONS_PATH}?${MODULE_QUERY_PARAM}=${encodeURIComponent(name)}`,
    );
  };

  if (loading && resolvedHasModule === null) return null;

  const effectiveHasModule =
    loading && resolvedHasModule !== null
      ? resolvedHasModule
      : Boolean(canAccessModule);

  const lockIconOffset = position === "overlay" ? { right: -14, top: -4 } : {};

  if (effectiveHasModule) {
    return children || null;
  }

  if (typeof renderGate === "function") {
    return renderGate({
      hasModule: effectiveHasModule,
      loading,
      moduleIdentifier: name,
      availableToTrial,
      onUpgrade: openModuleInAddons,
    });
  }

  if (!children) return null;

  return (
    <div
      className={joinClasses(
        styles.lockedWrapper,
        position === "end"
          ? styles.lockedWrapperEnd
          : styles.lockedWrapperOverlay,
      )}
      onClick={openModuleInAddons}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openModuleInAddons();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className={styles.lockedContent}>{children}</div>
      {showLockIcon && <LockIconBadge offset={lockIconOffset} />}
    </div>
  );
};

ModuleGate.propTypes = {
  name: string.isRequired,
  children: node,
  renderGate: func,
  showLockIcon: bool,
  position: oneOf(["end", "overlay"]),
  availableToTrial: bool,
  lockOnExpiredTrial: bool,
};

ModuleGate.defaultProps = {
  children: null,
  renderGate: undefined,
  showLockIcon: true,
  position: "end",
  availableToTrial: true,
  lockOnExpiredTrial: true,
};

export default ModuleGate;
